import { useMemo, ReactNode, useContext } from 'react';

// material-ui
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { createTheme, ThemeOptions, ThemeProvider, Theme } from '@mui/material/styles';
import { TypographyOptions } from '@mui/material/styles/createTypography';

// project import
import useConfig from 'hooks/useConfig';
import { CustomDashboardConfigContext } from 'contexts/CustomDashboardConfig';
import Palette from './palette';
import Typography from './typography';
import componentStyleOverrides from './compStyleOverride';
import customShadows from './shadows';
import defaultConfig from 'themeConfig';

// types
import { CustomShadowProps } from 'types/default-theme';
import { CustomBrandSettings } from 'types/dashboard-config';
import { AvailableFontsType } from 'utils/fonts';

interface Props {
    children: ReactNode;
}

export default function ThemeCustomization({ children }: Props) {
    const { borderRadius, navType, outlinedFilled, presetColor, rtlLayout } = useConfig();
    const { dashboardConfig } = useContext(CustomDashboardConfigContext);

    const configOptions: Partial<CustomBrandSettings> = useMemo<Partial<CustomBrandSettings>>(
        () => ({
            primaryColor: dashboardConfig?.primaryColor || defaultConfig.primaryColor,
            secondaryColor: dashboardConfig?.secondaryColor || defaultConfig.secondaryColor,
            fontFamily: dashboardConfig?.fontUrl || defaultConfig.fontFamily
        }),
        [dashboardConfig]
    );

    const theme: Theme = useMemo<Theme>(
        () => Palette(navType, presetColor, configOptions.primaryColor, configOptions.secondaryColor),
        [navType, presetColor, configOptions.primaryColor, configOptions.secondaryColor]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const themeTypography: TypographyOptions = useMemo<TypographyOptions>(
        () => Typography(theme, borderRadius, configOptions.fontFamily as AvailableFontsType),
        [theme, borderRadius, configOptions.fontFamily]
    );
    const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(() => customShadows(navType, theme), [navType, theme]);

    const themeOptions: ThemeOptions = useMemo(
        () => ({
            direction: rtlLayout ? 'rtl' : 'ltr',
            palette: theme.palette,
            mixins: {
                toolbar: {
                    minHeight: '48px',
                    padding: '16px',
                    '@media (min-width: 600px)': {
                        minHeight: '48px'
                    }
                }
            },
            typography: themeTypography,
            customShadows: themeCustomShadows
        }),
        [rtlLayout, theme, themeCustomShadows, themeTypography]
    );

    const themes: Theme = createTheme(themeOptions);
    themes.components = useMemo(
        () => componentStyleOverrides(themes, borderRadius, outlinedFilled),
        [themes, borderRadius, outlinedFilled]
    );

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={themes}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
